<template>
  <div class="task-list-item list-item clearfix" :class="{ 'is-new' : taskIsNew }" @click="gotoTask">
    <div class="drag-handle">
      <div class="dots">
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
      </div>
    </div>
    <client-code v-if="showClientCode && !task.client_icon_url" :code="task.client_code" :seed="task.client_name"/>
    <img v-if="showClientCode && task.client_icon_url" :src="task.client_icon_url" class="client-code"/>
    <div class="info">
      <div class="project" v-if="!project"><b>{{ task.client_name }} - {{ task.project_code }}</b></div>
      <div class="title"></div>
      <div class="title"><span v-if="showTaskProjectName">{{ task.project_name }} - </span>{{ task.title }}</div>
      <div class="user" v-if="!user">{{ task.user_full_name  }}</div>
    </div>
    <project-avatars v-if="!this.user || task.group_task" :shorten="true" :task="task" size="small" />
    <div class="timer">
      <el-tag size="mini" v-if="showStatusTag && !canEditStatus" :type="taskStatusClass">{{ statuses[task.status] }}</el-tag>
      <div v-if="showStatusTag && canEditStatus"
           class="status-wrap">
        <el-tag size="mini" :type="taskStatusClass" @click.stop="editTaskStatus()">{{ statuses[task.status] }}</el-tag>
        <el-select
                   v-model="editTaskStatusValue"
                   ref="taskStatusSelect"
                   size="small"
                   class="status-select"
                   @blur="onTaskStatusBlur"
                   @focus="onTaskStatusFocus"
                   @change="onTaskStatusChange"
        >
          <el-option
            v-for="(item, key) in statuses"
            :key="key"
            :label="item"
            :value="key">
          </el-option>
        </el-select>
      </div>

      <div>
        <icon name="regular/clock" :class="{ 'active-task': task.is_log_active }" />
        <span class="total-time" :class="{ 'active-task': task.is_log_active }">{{ secondsToTime(task.total_time) }}</span>
      </div>

      <el-button type="success"
                 size="mini"
                 round
                 class="timer-btn pull-right"
                 @click.stop.prevent="startTimer"
                 :disabled="!!runningLog"
                 v-if="showStartButton">
        <icon name="play" class="play" v-if="!showSpinnerButton" />
        <icon name="circle-notch" spin v-if="showSpinnerButton" />
      </el-button>
    </div>
  </div>
</template>

<script>
  import { mixin as LogTimes } from '../../../mixins/LogTimes'
  import { mixin as Utils } from '../../../mixins/Utils'
  import { taskStatuses } from '../../../util/taskStatuses'
  export default {
    name: 'task-list-item',
    mixins: [LogTimes, Utils],
    props: {
      showClientCode: { default: false },
      showStatusTag: { default: false },
      showStartButton: { default: false },
      showTaskProjectName: { default: false },
      task: null,
      project: null, // Scope the Tasks to a single project. Also affects the TaskCRUD default Project Selection
      user: null // Scope the Tasks to a single user. Hides the user line on a task
    },
    data () {
      return {
        editTaskStatusValue: this.task.status,
        showEditTaskStatus: false,
        showSpinnerButton: false
      }
    },
    methods: {
      gotoTask () {
        this.$router.push({name: 'task', params: { task_id: this.task.id }})
      },
      startTimer () {
        if (this.runningLog) {
          // CANNOT START MULTIPLE TASKS
        } else {
          // Omitting 'start' will tell the API to set the start to the current datetime
          var object = {
            'user_id': this.$store.state.users.currentUser.id,
            'task_id': this.task.id
          }
          this.$store.dispatch('createLog', object)
          // Attempt to clear any notifications for this task
          this.$store.dispatch('clearTaskNotifications', this.task)
        }
      },
      editTaskStatus () {
        if (this.canEditStatus) {
          if (!this.showEditTaskStatus) {
            this.$refs['taskStatusSelect'].toggleMenu()
            this.showEditTaskStatus = true
          } else {
            this.$refs['taskStatusSelect'].blur()
            this.showEditTaskStatus = false
          }
        } else {
          return true
        }
      },
      onTaskStatusChange (newStatus) {
        // Update the Task in the API
        let object = {
          id: this.task.id, // We only need ID and new status for the PUT request
          status: newStatus
        }

        this.$store.dispatch('updateTask', object)

        // Update the local task to immediately update UI
        this.task.status = newStatus
      },
      onTaskStatusFocus () {
        this.showEditTaskStatus = true
      },
      onTaskStatusBlur () {
        this.showEditTaskStatus = false
      }
    },
    computed: {
      taskStatusClass () {
        switch (this.task.status) {
          case 0:
            return ''
          case 1:
            return 'success'
          case 2:
          case 3:
            return 'warning'
          case 4:
          case 5:
          case 6:
            return 'danger'
          default:
            return ''
        }
      },
      taskIsNew () {
        for (let k in this.notifications) {
          if (this.notifications.hasOwnProperty(k) &&
            this.notifications[k].user_id === this.currentUser.id &&
            this.notifications[k].task_id === this.task.id) {
            return true
          }
        }
        return false
      },
      statuses () {
        return taskStatuses
      },
      canEditStatus () {
        return this.task.user_id === this.currentUser.id || this.isAdmin
      },

      // Vuex Mappings
      notifications () {
        return this.$store.state.notifications.objects
      },
      currentUser () {
        return this.$store.state.users.currentUser
      },
      isAdmin () {
        return this.$store.getters.isAdmin
      },
      runningLog () {
        return this.$store.getters.runningLog
      },
      createLogInProgress () {
        return this.$store.state.logs.createInProgress
      }
    },
    watch: {
      'createLogInProgress' () {
        if (this.createLogInProgress && this.createLogInProgress.task_id === this.task.id) {
          this.showSpinnerButton = true
        } else {
          this.showSpinnerButton = false
        }
      }
    }
  }
</script>

<style lang="less" rel="stylesheet/less" scoped>
  .task-list-item {
    position: relative;
    padding-left: 35px;
    cursor: pointer;

    &.is-new {
      background-color: fade(#FDBD10, 4%);
      border: 1px solid fade(#FDBD10, 20%);
      box-shadow: 0 2px 5px fade(#FDBD10, 60%);
    }

    .drag-handle {
      left: 0;
      position: absolute;
      z-index: 3;
      width: 30px;
      height: 100%;
      cursor: ns-resize;
      .dots {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        height: 20px;
        width: 10px;
        left: 8px;
      }
      .dot {
        width: 2px;
        height: 2px;
        border-radius: 4px;
        background-color: #B8B8B8;
        position: absolute;

        &:nth-child(0n+1) {
          top: 0;
          left: 0
        }
        &:nth-child(0n+2) {
          top: 0;
          right: 0
        }

        &:nth-child(0n+3) {
          top: 50%;
          transform: translateY(-50%);
          left: 0
        }
        &:nth-child(0n+4) {
          top: 50%;
          transform: translateY(-50%);
          right: 0
        }
        &:nth-child(0n+5) {
          bottom: 0;
          left: 0
        }
        &:nth-child(0n+6) {
          bottom: 0;
          right: 0
        }
      }
    }
    &.sortable-ghost {
      opacity: 0;
    }
    .info {
      flex: 1;
      white-space: normal;
      padding-right: 10px;
    }

    .avatars {
      margin-right: 15px;
    }

    .project,
    .user {
      font-size: 0.9em;
    }

    .timer {
      margin-top: 0;
      color: #606266;
      display: flex;
      align-items: center;
      @media screen and (max-width: 520px) {
        flex-direction: column;
      }
    }
    .status-wrap {
      position: relative;
      cursor: pointer;
    }
    .status-select {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      z-index: -1;
    }
    .timer-btn {
      margin-left: 15px;
      width: 36px;
      height: 36px;
      border-radius: 22px;
      text-align: center;
      box-shadow: 2px 2px 5px fade(#67c23a, 40%);
      padding: 7px 11px 7px 13px;
      &.is-disabled {
        box-shadow: 2px 2px 5px fade(#b3e19d, 40%);
      }
      .fa-icon {
        vertical-align: middle;
        max-width: 100%;
        width: 28px;
        margin: 0;

        &.play {

        }
      }
    }
  }
</style>
